import { useCallback, useEffect, useState } from 'react';
import { ProjectService } from '../../lib/projectService';
import { validateScriptBlocks, validateScriptDocument } from '../../lib/scriptPersistence';
import { readFDXFormatSource, type ScriptFormatSource } from '../../lib/scriptFormat';
import { layoutScreenplay } from '../../lib/scriptLayout';
import { blockStartPages } from '../../lib/scriptOutline';
import type { ScriptBlockData } from '../story/ScriptBlock';

// Scenes of the project's screenplay placed on pages, for the outline strip's Script lane.
// Uses the Story editor's own layout engine, so positions match its page breaks.

export interface OutlineScene {
    blockId: string;
    heading: string;
    start: number;   // pages from the top of page 1 (fractional)
    length: number;  // pages
}

export interface ScriptOutline {
    scenes: OutlineScene[];
    pageCount: number;
}

const EMPTY_OUTLINE: ScriptOutline = { scenes: [], pageCount: 0 };

export function scenesFromScript(blocks: readonly ScriptBlockData[], format: ScriptFormatSource): ScriptOutline {
    if (blocks.length === 0) return EMPTY_OUTLINE;
    const layout = layoutScreenplay(blocks, format);
    const startPages = blockStartPages(layout, format);
    const starts: Omit<OutlineScene, 'length'>[] = blocks
        .filter(b => b.type === 'scene-heading' && startPages.has(b.id))
        .map(b => ({ blockId: b.id, heading: b.content.trim() || 'Untitled scene', start: startPages.get(b.id)! }));

    const pageCount = layout.pages.length;
    const scenes = starts.map((scene, i) => ({
        ...scene,
        length: Math.max(0.05, (starts[i + 1]?.start ?? pageCount) - scene.start)
    }));
    return { scenes, pageCount };
}

// refreshKey: change it (e.g. the current route) to re-read the screenplay; pages stay
// mounted when switching between Idea and Story inside the studio.
export function useScriptScenes(projectId: string | null | undefined, refreshKey?: string): ScriptOutline {
    const [outline, setOutline] = useState<ScriptOutline>(EMPTY_OUTLINE);

    const load = useCallback(async (signal: { cancelled: boolean }) => {
        if (!projectId) {
            setOutline(EMPTY_OUTLINE);
            return;
        }
        try {
            const project = await ProjectService.loadScriptDocument(projectId);
            if (signal.cancelled) return;
            const blocks = validateScriptBlocks(project.script_content ?? []);
            const document = validateScriptDocument(project.script_document);
            setOutline(scenesFromScript(blocks, readFDXFormatSource(document.sourceFDX?.xml)));
        } catch (error) {
            // A screenplay that can't be read just leaves the Script lane empty
            console.error('Outline: could not read the screenplay', error);
            if (!signal.cancelled) setOutline(EMPTY_OUTLINE);
        }
    }, [projectId]);

    useEffect(() => {
        const signal = { cancelled: false };
        load(signal);
        // Pick up edits made on the Story page when the writer comes back to this tab
        const onVisible = () => {
            if (document.visibilityState === 'visible') load(signal);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            signal.cancelled = true;
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [load, refreshKey]);

    return outline;
}
